import { supabase } from '../lib/supabase';

/**
 * Error-surfacing pattern: see canonical doc-comment at the top of
 * src/services/clientService.js.
 *
 * createActivity is invoked from many other services as an audit-trail
 * write (snagService, taskService, auditService, taskPhotoService, ...)
 * and most of those callers `await` it but drop the return — i.e. they
 * intend fire-and-forget semantics but actually block on completion. That
 * caller-side cleanup is a separate card. This service's job is just to
 * make sure the real error is available when callers DO consume it.
 */
function surfaceError(scope, error) {
  console.error(`[activityService] ${scope} failed:`, {
    code: error?.code,
    message: error?.message,
    details: error?.details,
    hint: error?.hint,
  });
  return error;
}

export const activityService = {
  async createActivity(activityData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Defence in depth for the activities INSERT RLS policy
      // (`with_check (company_id = get_user_company_id())`). The policy
      // rejects with 42501 when the row's company_id is NULL, which makes
      // the omission look like a permissions error rather than a missing
      // field. SitePhotoUploadModal silently dropped activity rows for
      // ~28 days because of this exact pattern. When the caller doesn't
      // provide companyId, look it up from the user_profiles row — the
      // same fast path the RLS helper get_user_company_id() uses.
      let companyId = activityData?.companyId;
      if (!companyId) {
        const { data: profile, error: profileError } = await supabase
          ?.from('user_profiles')
          ?.select('company_id')
          ?.eq('id', user?.id)
          ?.maybeSingle();
        if (profileError) throw profileError;
        companyId = profile?.company_id || null;
      }
      if (!companyId) {
        const err = new Error('createActivity: no company_id available for the current user');
        err.code = 'NO_COMPANY_ID';
        throw err;
      }

      const { data, error } = await supabase?.from('activities')?.insert({
          company_id: companyId,
          site_id: activityData?.siteId || null,
          activity_type: activityData?.activityType,
          title: activityData?.title,
          description: activityData?.description || '',
          user_id: user?.id,
          photo_url: activityData?.photoUrl || null,
          metadata: activityData?.metadata || {},
          severity_level: activityData?.severityLevel || null,
          related_entity_id: activityData?.relatedEntityId || null
        })?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: surfaceError('createActivity', error) };
    }
  },

  async getActivitiesBySite(siteId, filters = {}) {
    try {
      let query = supabase
        ?.from('activities')
        ?.select(`
          *,
          user:user_profiles!activities_user_id_fkey(id, full_name, role, avatar_url),
          site:sites!activities_site_id_fkey(id, site_name)
        `)
        ?.eq('site_id', siteId)
        ?.order('created_at', { ascending: false });

      if (filters?.activityType && filters?.activityType !== 'all') {
        if (Array.isArray(filters.activityType)) {
          query = query?.in('activity_type', filters.activityType);
        } else {
          query = query?.eq('activity_type', filters.activityType);
        }
      }

      if (filters?.userId && filters?.userId !== 'all') {
        query = query?.eq('user_id', filters.userId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', new Date(filters.dateFrom).toISOString());
      }

      if (filters?.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query?.lte('created_at', endDate.toISOString());
      }

      if (filters?.limit) {
        query = query?.limit(filters?.limit);
      } else {
        query = query?.limit(200);
      }

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getActivitiesBySite', error) };
    }
  },

  async getAllActivities(filters = {}) {
    try {
      let query = supabase
        ?.from('activities')
        ?.select(`
          *,
          user:user_profiles!activities_user_id_fkey(id, full_name, role, avatar_url),
          site:sites!activities_site_id_fkey(id, site_name)
        `)
        ?.order('created_at', { ascending: false });

      if (filters?.activityType && filters?.activityType !== 'all') {
        if (Array.isArray(filters.activityType)) {
          query = query?.in('activity_type', filters.activityType);
        } else {
          query = query?.eq('activity_type', filters.activityType);
        }
      }

      if (filters?.userId && filters?.userId !== 'all') {
        query = query?.eq('user_id', filters.userId);
      }

      if (filters?.dateFrom) {
        query = query?.gte('created_at', new Date(filters.dateFrom).toISOString());
      }

      if (filters?.dateTo) {
        const endDate = new Date(filters.dateTo);
        endDate.setHours(23, 59, 59, 999);
        query = query?.lte('created_at', endDate.toISOString());
      }

      query = query?.limit(filters?.limit || 50);

      const { data, error } = await query;
      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getAllActivities', error) };
    }
  },

  /**
   * Subscribe to real-time activity inserts for a specific site.
   * Returns an unsubscribe function.
   */
  subscribeToSiteActivities(siteId, onNewActivity) {
    const channel = supabase
      .channel(`activities:site:${siteId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activities',
          filter: `site_id=eq.${siteId}`
        },
        async (payload) => {
          // Enrich the new record with user + site joins
          try {
            const { data, error } = await supabase
              .from('activities')
              .select(`
                *,
                user:user_profiles!activities_user_id_fkey(id, full_name, role, avatar_url),
                site:sites!activities_site_id_fkey(id, site_name)
              `)
              .eq('id', payload.new.id)
              .single();
            if (error) throw error;
            if (data) onNewActivity(data);
          } catch (err) {
            console.warn('[activityService] subscribeToSiteActivities enrichment failed, using raw payload:', { code: err?.code, message: err?.message });
            onNewActivity(payload.new);
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  },

  /**
   * Subscribe to real-time activity inserts for all company activities.
   * Returns an unsubscribe function.
   */
  subscribeToAllActivities(onNewActivity) {
    const channel = supabase
      .channel('activities:all')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activities'
        },
        async (payload) => {
          try {
            const { data, error } = await supabase
              .from('activities')
              .select(`
                *,
                user:user_profiles!activities_user_id_fkey(id, full_name, role, avatar_url),
                site:sites!activities_site_id_fkey(id, site_name)
              `)
              .eq('id', payload.new.id)
              .single();
            if (error) throw error;
            if (data) onNewActivity(data);
          } catch (err) {
            console.warn('[activityService] subscribeToAllActivities enrichment failed, using raw payload:', { code: err?.code, message: err?.message });
            onNewActivity(payload.new);
          }
        }
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  },

  /**
   * Fetch distinct users who have activity on a given site (for worker/user filter dropdown).
   */
  async getActivityUsersBySite(siteId) {
    try {
      const { data, error } = await supabase
        .from('activities')
        .select('user_id, user:user_profiles!activities_user_id_fkey(id, full_name, role)')
        .eq('site_id', siteId)
        .not('user_id', 'is', null);

      if (error) throw error;

      const seen = new Set();
      const users = [];
      for (const row of (data || [])) {
        if (row?.user_id && !seen.has(row.user_id)) {
          seen.add(row.user_id);
          users.push(row.user);
        }
      }
      return { data: users, error: null };
    } catch (error) {
      return { data: [], error: surfaceError('getActivityUsersBySite', error) };
    }
  }
};

export const notificationService = {
  async getAllNotifications() {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.order('created_at', { ascending: false })?.limit(50);

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      console.error('[activityService:notificationService] getAllNotifications failed:', { code: error?.code, message: error?.message });
      return { data: [], error };
    }
  },

  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('[activityService:notificationService] markAsRead failed:', { code: error?.code, message: error?.message });
      return { data: null, error };
    }
  }
};
